/**
 * DRBFM Results Step Component
 *
 * Displays DRBFM analysis results:
 * - Summary statistics
 * - Design changes table
 * - Failure modes table
 * - Countermeasures table
 * - Export functionality
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Alert,
  Chip,
  TextField,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CompareArrows as ChangesIcon,
  Warning as FailuresIcon,
  Build as CountermeasuresIcon,
  PlaylistAdd as ConvertIcon,
  ThreeDRotation as ThreeDIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FailureMode, PredefinedPartLabel, SessionResultsResponse } from '../../../types';
import DRBFMTable from '../DRBFMTable';
import ConvertToVerificationDialog from '../ConvertToVerificationDialog';
import { CADModelSelect } from '../../cad/CADModelSelect';
import { parsePartLabelsJson } from '../../../utils/cadPartLabels';
import DRBFMDecisionMemoryPanel from '../DRBFMDecisionMemoryPanel';
import DRBFMDrawingEvidenceDialog from '../DRBFMDrawingEvidenceDialog';
import { translateWithFallback } from '../../../utils/translation';

interface DRBFMResultsStepProps {
  results: SessionResultsResponse | null;
  onExport: () => void;
  onNewAnalysis: () => void;
  isExporting: boolean;
  exportError: string | null;
  drbfmSessionId?: string;
  drbfmItemName?: string;
  onLinkCad?: (cadModelId: string, predefinedPartLabels?: PredefinedPartLabel[]) => Promise<void>;
  isLinkingCad?: boolean;
  linkCadError?: string | null;
  cadEnabled?: boolean | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`drbfm-tabpanel-${index}`}
      aria-labelledby={`drbfm-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const selectHighestRiskFailureMode = (
  failureModes: FailureMode[] | undefined,
): FailureMode | null => {
  if (!failureModes?.length) {
    return null;
  }

  return [...failureModes].sort((a, b) => {
    const rpnDiff = (b.rpnScore || 0) - (a.rpnScore || 0);
    if (rpnDiff !== 0) return rpnDiff;
    return (b.severityScore || 0) - (a.severityScore || 0);
  })[0];
};

const DRBFMResultsStep: React.FC<DRBFMResultsStepProps> = ({
  results,
  onExport,
  onNewAnalysis,
  isExporting,
  exportError,
  drbfmSessionId,
  drbfmItemName,
  onLinkCad,
  isLinkingCad = false,
  linkCadError = null,
  cadEnabled = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(results?.failureModes?.length ? 1 : 0);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedCadModelId, setSelectedCadModelId] = useState<string | undefined>();
  const [partLabelsJson, setPartLabelsJson] = useState('');
  const [partLabelsError, setPartLabelsError] = useState<string | null>(null);
  const [selectedFailureModeId, setSelectedFailureModeId] = useState<string | undefined>(
    () => selectHighestRiskFailureMode(results?.failureModes)?.id,
  );
  const [autoSelectedTabSessionId, setAutoSelectedTabSessionId] = useState<string | undefined>(
    () => results?.session?.id,
  );
  const [drawingEvidenceFailureMode, setDrawingEvidenceFailureMode] = useState<FailureMode | null>(
    null,
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const highestRiskFailureMode = useMemo(
    () => selectHighestRiskFailureMode(results?.failureModes),
    [results?.failureModes],
  );
  const selectedFailureMode = useMemo(
    () => (
      results?.failureModes?.find((failureMode) => failureMode.id === selectedFailureModeId)
      || highestRiskFailureMode
    ),
    [highestRiskFailureMode, results?.failureModes, selectedFailureModeId],
  );

  useEffect(() => {
    if (
      results?.session.id
      && results.failureModes?.length
      && autoSelectedTabSessionId !== results.session.id
    ) {
      setTabValue(1);
      setAutoSelectedTabSessionId(results.session.id);
    }
  }, [autoSelectedTabSessionId, results?.failureModes?.length, results?.session.id]);

  useEffect(() => {
    const currentSelectionStillExists = results?.failureModes?.some(
      (failureMode) => failureMode.id === selectedFailureModeId,
    );
    if (!currentSelectionStillExists) {
      setSelectedFailureModeId(highestRiskFailureMode?.id);
    }
  }, [highestRiskFailureMode?.id, results?.failureModes, selectedFailureModeId]);

  // 3D bulk viewer button — derive cad_model_id from any anchored failure mode.
  // The button is hidden when no failure mode carries a CAD anchor (TG #673).
  const anchoredCadModelId = results?.failureModes?.find((fm) => fm.cadAnchor)?.cadAnchor
    ?.cadModelId;
  const unlinkedFailureModesCount = results?.failureModes?.filter((fm) => !fm.cadAnchor).length || 0;
  const canViewAll3D = Boolean(anchoredCadModelId && drbfmSessionId);
  const canOfferLinkCad = cadEnabled !== false && Boolean(
    drbfmSessionId && onLinkCad && unlinkedFailureModesCount > 0,
  );

  const handleView3DAll = () => {
    if (!canViewAll3D) return;
    const params = new URLSearchParams();
    params.set('cad_model', anchoredCadModelId!);
    params.set('drbfm_session', drbfmSessionId!);
    navigate(`/cad-viewer?${params.toString()}`);
  };

  const handleLinkCad = async () => {
    if (!selectedCadModelId || !onLinkCad) return;
    try {
      const labels = parsePartLabelsJson(partLabelsJson);
      setPartLabelsError(null);
      await onLinkCad(selectedCadModelId, labels.length > 0 ? labels : undefined);
    } catch (err) {
      setPartLabelsError(err instanceof Error ? err.message : t('drbfm.cadLink.parseError', '部位ラベルJSONを読み取れません'));
    }
  };

  // No results state
  if (!results) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('drbfm.results.noResults')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={onNewAnalysis}
          sx={{ mt: 2 }}
        >
          {t('drbfm.results.startNew')}
        </Button>
      </Box>
    );
  }

  // Calculate statistics
  const designChangesCount = results.designChanges?.length || 0;
  const failureModesCount = results.failureModes?.length || 0;
  const countermeasuresCount = results.countermeasures?.length || 0;

  // Count critical failure modes
  const criticalCount = results.failureModes?.filter(
    (fm) => fm.severity === 'critical' || fm.severityScore >= 8
  ).length || 0;

  // Count high priority countermeasures
  const highPriorityCount = results.countermeasures?.filter(
    (cm) => cm.priority === 1 || cm.effectivenessScore >= 8
  ).length || 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {t('drbfm.results.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('drbfm.results.description')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onNewAnalysis}
          >
            {t('drbfm.results.newAnalysis')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ConvertIcon />}
            onClick={() => setConvertDialogOpen(true)}
            color="success"
          >
            {t('drbfm.convert.buttonLabel', '検証項目に変換')}
          </Button>
          {canViewAll3D && (
            <Button
              variant="outlined"
              startIcon={<ThreeDIcon />}
              onClick={handleView3DAll}
              color="info"
          >
            {t('drbfm.cadLink.bulkView', '3Dで一括表示')}
          </Button>
          )}
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={onExport}
            disabled={isExporting}
          >
            {isExporting ? t('drbfm.results.exporting') : t('drbfm.results.export')}
          </Button>
        </Box>
      </Box>

      {/* Export Error */}
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}

      {linkCadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {linkCadError}
        </Alert>
      )}

      {canOfferLinkCad && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ minWidth: 240 }}>
              {anchoredCadModelId
                ? translateWithFallback(t, 'drbfm.cadLink.unlinkedCount', `未リンクの失敗モードが${unlinkedFailureModesCount}件あります。`, {
                    count: unlinkedFailureModesCount,
                  })
                : t('drbfm.cadLink.noLinks', 'このDRBFM結果にはまだCADリンクがありません。')}
            </Typography>
            <Box sx={{ minWidth: 280, flex: '1 1 280px' }}>
              <CADModelSelect
                value={selectedCadModelId || ''}
                onChange={setSelectedCadModelId}
                disabled={isLinkingCad}
                helperText={t('drbfm.cadLink.linkHelper', '選択したCADモデルに失敗モードをリンクします')}
              />
            </Box>
            <Box sx={{ minWidth: 280, flex: '1 1 280px' }}>
              <TextField
                label={t('drbfm.cadLink.partLabelsJson', '部位ラベルJSON')}
                value={partLabelsJson}
                onChange={(event) => {
                  setPartLabelsJson(event.target.value);
                  if (partLabelsError) setPartLabelsError(null);
                }}
                fullWidth
                multiline
                minRows={2}
                disabled={isLinkingCad}
                error={!!partLabelsError}
                helperText={
                  partLabelsError
                  || t('drbfm.cadLink.partLabelsHelper', 'cover_part_labels.json全体、またはparts配列を貼り付けると優先ラベルとして使います')
                }
              />
            </Box>
            <Button
              variant="contained"
              color="info"
              onClick={handleLinkCad}
              disabled={!selectedCadModelId || isLinkingCad}
              disableRipple
            >
              {isLinkingCad
                ? t('drbfm.cadLink.linking', 'リンク中...')
                : t('drbfm.cadLink.link', 'CADにリンク')}
            </Button>
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <ChangesIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary">
                {designChangesCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('drbfm.stats.designChanges')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <FailuresIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {failureModesCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('drbfm.stats.failureModes')}
              </Typography>
              {criticalCount > 0 && (
                <Chip
                  label={t('drbfm.stats.critical', { count: criticalCount })}
                  color="error"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <CountermeasuresIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {countermeasuresCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('drbfm.stats.countermeasures')}
              </Typography>
              {highPriorityCount > 0 && (
                <Chip
                  label={t('drbfm.stats.highPriority', { count: highPriorityCount })}
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Results Tabs */}
      <Card variant="outlined">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label={t('drbfm.results.tabsAria')}>
            <Tab
              icon={<ChangesIcon />}
              iconPosition="start"
              label={`${t('drbfm.table.designChanges')} (${designChangesCount})`}
            />
            <Tab
              icon={<FailuresIcon />}
              iconPosition="start"
              label={`${t('drbfm.table.failureModes')} (${failureModesCount})`}
            />
            <Tab
              icon={<CountermeasuresIcon />}
              iconPosition="start"
              label={`${t('drbfm.table.countermeasures')} (${countermeasuresCount})`}
            />
          </Tabs>
        </Box>

        <CardContent>
          <TabPanel value={tabValue} index={0}>
            <DRBFMTable
              type="designChanges"
              data={results.designChanges || []}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid size={{ xs: 12, lg: 8 }}>
                <DRBFMTable
                  type="failureModes"
                  data={results.failureModes || []}
                  drbfmSessionId={drbfmSessionId}
                  selectedFailureModeId={selectedFailureMode?.id}
                  onSelectFailureMode={(failureMode) => setSelectedFailureModeId(failureMode.id)}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 4 }}>
                <DRBFMDecisionMemoryPanel
                  failureMode={selectedFailureMode || null}
                  onOpenDrawingEvidence={setDrawingEvidenceFailureMode}
                />
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <DRBFMTable
              type="countermeasures"
              data={results.countermeasures || []}
            />
          </TabPanel>
        </CardContent>
      </Card>

      {/* Convert to Verification Dialog */}
      {drbfmSessionId && results && (
        <ConvertToVerificationDialog
          open={convertDialogOpen}
          onClose={() => setConvertDialogOpen(false)}
          drbfmSessionId={drbfmSessionId}
          drbfmItemName={drbfmItemName || ''}
          failureModes={results.failureModes || []}
          designChanges={results.designChanges || []}
          countermeasures={results.countermeasures || []}
        />
      )}
      <DRBFMDrawingEvidenceDialog
        open={Boolean(drawingEvidenceFailureMode)}
        failureMode={drawingEvidenceFailureMode}
        onClose={() => setDrawingEvidenceFailureMode(null)}
      />
    </Box>
  );
};

export default DRBFMResultsStep;
